import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { CONTACT_INFO } from '../data';

export default function PageRenderer() {
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derive slug from pathname
  const slug = location.pathname.substring(1) || 'om-vester-bo';

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    // Fetch page by slug from public CMS endpoint
    fetch(`https://api.dev.auxo.dk/public/by-slug/${slug}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Siden blev ikke fundet');
        }
        return res.json();
      })
      .then((data) => {
        if (isMounted) {
          setPage(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col justify-center items-center">
        <div className="h-12 w-12 border-4 border-slate-200 border-t-primary rounded-full animate-spin mb-4" />
        <p className="text-slate-500 text-sm font-medium">Henter indhold...</p>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-[60vh] flex flex-col justify-center items-center text-center px-4">
        <div className="h-16 w-16 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 text-2xl mb-4 border border-rose-100">
          <i className="fa-solid fa-circle-exclamation" />
        </div>
        <h2 className="font-serif text-2xl font-bold text-secondary mb-2">Siden blev ikke fundet</h2>
        <p className="text-slate-500 max-w-md mb-6">
          Beklager, men vi kunne ikke finde den side, du leder efter. Den kan være flyttet eller slettet.
        </p>
        <Link to="/" className="px-6 py-3 rounded-full text-white bg-primary hover:bg-primary-dark font-semibold shadow-md transition-colors">
          Gå til forsiden
        </Link>
      </div>
    );
  }

  // Check if page has image
  const hasImage = page.image && page.image.length > 0;
  const imageUrl = hasImage ? page.image[0] : null;

  return (
    <div className="py-12 bg-slate-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex mb-8 text-xs text-slate-500" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/" className="hover:text-primary transition-colors flex items-center gap-1.5">
                <i className="fa-solid fa-house text-[10px]" />
                <span>Forside</span>
              </Link>
            </li>
            <li>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-chevron-right text-[8px] text-slate-400" />
                <span className="font-medium text-slate-700">{page.title}</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar */}
          <Sidebar />

          {/* Main Content Area */}
          <div className="flex-1 bg-white rounded-3xl p-8 lg:p-12 border border-slate-100 shadow-sm">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-secondary mb-8 pb-4 border-b border-slate-100">
              {page.title}
            </h1>

            {/* Page Image */}
            {imageUrl && (
              <div className="mb-8 rounded-2xl overflow-hidden border border-slate-100 shadow-inner max-h-[450px]">
                <img
                  src={imageUrl}
                  alt={page.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* HTML Content rendered safely */}
            <div 
              className="prose prose-slate max-w-none prose-headings:font-serif prose-headings:text-secondary prose-a:text-primary hover:prose-a:text-primary-dark prose-strong:text-secondary prose-table:border prose-table:border-slate-100 prose-th:bg-slate-50 prose-th:p-3 prose-td:p-3 prose-td:border-t prose-td:border-slate-100"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />

            {slug === 'tilsyn' && (
              <div className="mt-12 p-6 bg-teal-50/30 rounded-2xl border border-teal-100/50 flex flex-col sm:flex-row justify-between items-center gap-6">
                <div>
                  <h4 className="font-serif text-lg font-bold text-secondary mb-1">Har du spørgsmål til tilsyn?</h4>
                  <p className="text-sm text-slate-600">Kontakt os for yderligere information om vores tilsynsydelser.</p>
                </div>
                <Link to="/contact" className="px-6 py-3 rounded-full text-white bg-primary hover:bg-primary-dark font-semibold shadow-md transition-colors whitespace-nowrap">
                  Kontakt os her
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
